/**
 * Monitoring — State Agency role screen
 * FR-S11 to FR-S13
 */
import React, { useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import KPICard from '../../components/shared/KPICard';
import {
  gujaratStateKPIs,
  districtMonitoringData,
  discomMonitoringData,
  circleMonitoringData,
} from '../../data/mockData';

// Gujarat-scoped data
const gujaratDistricts = districtMonitoringData.filter(d => d.state === 'Gujarat');
const gujaratDISCOMs   = discomMonitoringData.filter(d => d.state === 'Gujarat');

function txCls(v: number) {
  return v >= 95 ? 'text-green-600 font-semibold' :
         v >= 85 ? 'text-amber-600 font-semibold' :
                   'text-red-600 font-semibold';
}

export default function StateMonitoring() {
  // District table drill-down: null = All Districts, string = district name
  const [selectedDistrict, setSelectedDistrict] = useState<string | null>(null);
  // DISCOM table drill-down: null = All DISCOMs, string = DISCOM name
  const [selectedDISCOM, setSelectedDISCOM]     = useState<string | null>(null);

  const circleRows = useMemo(() => {
    if (!selectedDISCOM) return [];
    return circleMonitoringData.filter(c => c.discom === selectedDISCOM);
  }, [selectedDISCOM]);

  // KPI aggregates
  const totalActiveAlarms  = gujaratStateKPIs.alarms;
  const criticalAlarms     = Math.round(totalActiveAlarms * 0.18); // mock 18% critical
  const plantsOffline      = gujaratStateKPIs.offlineFaultCount;
  const avgDataTx          = parseFloat(
    (gujaratDISCOMs.reduce((s, d) => s + d.dataTxPercent, 0) / gujaratDISCOMs.length).toFixed(1)
  );
  const certsExpiring      = gujaratDISCOMs.reduce((s, d) => s + d.certsExpiring, 0);

  return (
    <div className="p-6 space-y-6">

      {/* ── 5 KPI cards ── */}
      <div className="flex gap-4">
        <KPICard
          title="Active Alarms"
          value={totalActiveAlarms.toString()}
          subtext="Across Gujarat"
          subtextColor={totalActiveAlarms > 100 ? 'red' : 'default'}
          bgColor={totalActiveAlarms > 100 ? 'bg-red-50' : undefined}
        />
        <KPICard
          title="Critical Alarms"
          value={criticalAlarms.toString()}
          subtext="Priority 1"
          subtextColor="red"
          bgColor="bg-red-50"
        />
        <KPICard
          title="Plants Offline"
          value={plantsOffline.toLocaleString()}
          subtext="Requires attention"
          subtextColor="red"
          bgColor="bg-red-50"
        />
        <KPICard
          title="Data Transmission"
          value={`${avgDataTx}%`}
          subtext="Avg across DISCOMs"
          subtextColor={avgDataTx >= 95 ? 'green' : avgDataTx >= 85 ? 'default' : 'red'}
        />
        <KPICard
          title="Certs Expiring ≤30d"
          value={certsExpiring.toString()}
          subtext="Across all Gujarat DISCOMs"
          subtextColor={certsExpiring > 5 ? 'red' : 'default'}
        />
      </div>

      {/* ── District compliance table ── */}
      <div className="bg-white rounded-card border border-border shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          {selectedDistrict ? (
            <div className="flex items-center gap-1 text-xs">
              <button onClick={() => setSelectedDistrict(null)} className="text-accent hover:underline font-medium">All Districts</button>
              <ChevronRight size={11} className="text-text-tertiary" />
              <span className="font-medium text-text-primary">{selectedDistrict}</span>
            </div>
          ) : (
            <p className="text-xs font-semibold text-text-primary">District Compliance — Gujarat</p>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-secondary border-b border-border">
              <tr>
                <th className="text-left py-2.5 px-4 text-text-secondary font-semibold">District</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Data Tx %</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Plants Offline</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Active Alarms</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Certs Expiring</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Firmware Outdated</th>
              </tr>
            </thead>
            <tbody>
              {gujaratDistricts.map(d => (
                <tr key={d.district} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                  <td className="py-2.5 px-4">
                    <span className="font-medium text-text-primary">{d.district}</span>
                  </td>
                  <td className={`py-2.5 px-3 text-right ${txCls(d.dataTxPercent)}`}>{d.dataTxPercent.toFixed(1)}%</td>
                  <td className={`py-2.5 px-3 text-right ${d.plantsOffline > 0 ? 'text-red-600 font-semibold' : 'text-text-secondary'}`}>{d.plantsOffline}</td>
                  <td className={`py-2.5 px-3 text-right ${d.activeAlarms > 10 ? 'text-amber-600 font-semibold' : 'text-text-secondary'}`}>{d.activeAlarms}</td>
                  <td className={`py-2.5 px-3 text-right ${d.certsExpiring > 0 ? 'text-amber-600 font-semibold' : 'text-text-secondary'}`}>{d.certsExpiring}</td>
                  <td className={`py-2.5 px-3 text-right ${d.firmwareOutdated > 0 ? 'text-text-secondary' : 'text-green-600'}`}>{d.firmwareOutdated}</td>
                </tr>
              ))}
              {gujaratDistricts.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-xs text-text-tertiary">No district data available.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── DISCOM compliance table ── */}
      <div className="bg-white rounded-card border border-border shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          {selectedDISCOM ? (
            <div className="flex items-center gap-1 text-xs">
              <button onClick={() => setSelectedDISCOM(null)} className="text-accent hover:underline font-medium">All DISCOMs</button>
              <ChevronRight size={11} className="text-text-tertiary" />
              <span className="font-medium text-text-primary">{selectedDISCOM}</span>
            </div>
          ) : (
            <p className="text-xs font-semibold text-text-primary">DISCOM Compliance — Gujarat</p>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-secondary border-b border-border">
              <tr>
                <th className="text-left py-2.5 px-4 text-text-secondary font-semibold">
                  {selectedDISCOM ? 'Circle' : 'DISCOM'}
                </th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Data Tx %</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Plants Offline</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Active Alarms</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Certs Expiring</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Firmware Outdated</th>
              </tr>
            </thead>
            <tbody>
              {!selectedDISCOM ? (
                gujaratDISCOMs.map(d => (
                  <tr key={d.discom} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                    <td className="py-2.5 px-4">
                      <button
                        onClick={() => setSelectedDISCOM(d.discom)}
                        className="font-medium text-accent hover:underline text-left"
                      >
                        {d.discom}
                      </button>
                    </td>
                    <td className={`py-2.5 px-3 text-right ${txCls(d.dataTxPercent)}`}>{d.dataTxPercent.toFixed(1)}%</td>
                    <td className={`py-2.5 px-3 text-right ${d.plantsOffline > 0 ? 'text-red-600 font-semibold' : 'text-text-secondary'}`}>{d.plantsOffline}</td>
                    <td className={`py-2.5 px-3 text-right ${d.activeAlarms > 20 ? 'text-amber-600 font-semibold' : 'text-text-secondary'}`}>{d.activeAlarms}</td>
                    <td className={`py-2.5 px-3 text-right ${d.certsExpiring > 0 ? 'text-amber-600 font-semibold' : 'text-text-secondary'}`}>{d.certsExpiring}</td>
                    <td className={`py-2.5 px-3 text-right ${d.firmwareOutdated > 0 ? 'text-text-secondary' : 'text-green-600'}`}>{d.firmwareOutdated}</td>
                  </tr>
                ))
              ) : (
                circleRows.length > 0 ? circleRows.map(c => (
                  <tr key={c.circle} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                    <td className="py-2.5 px-4 font-medium text-text-primary">{c.circle}</td>
                    <td className={`py-2.5 px-3 text-right ${txCls(c.dataTxPercent)}`}>{c.dataTxPercent.toFixed(1)}%</td>
                    <td className={`py-2.5 px-3 text-right ${c.plantsOffline > 0 ? 'text-red-600 font-semibold' : 'text-text-secondary'}`}>{c.plantsOffline}</td>
                    <td className={`py-2.5 px-3 text-right ${c.activeAlarms > 5 ? 'text-amber-600 font-semibold' : 'text-text-secondary'}`}>{c.activeAlarms}</td>
                    <td className={`py-2.5 px-3 text-right ${c.certsExpiring > 0 ? 'text-amber-600 font-semibold' : 'text-text-secondary'}`}>{c.certsExpiring}</td>
                    <td className={`py-2.5 px-3 text-right ${c.firmwareOutdated > 0 ? 'text-text-secondary' : 'text-green-600'}`}>{c.firmwareOutdated}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-xs text-text-tertiary">
                      No circle data available for {selectedDISCOM}.
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
